import { addDays, type DateRange } from "@prismicio/editor-support/Date"
import {
  Box,
  Card,
  CardContent,
  Separator,
  Skeleton,
  Text,
  type ThemeKeys,
  Tooltip,
} from "@prismicio/editor-ui"
import type { ReactNode } from "react"

import type { Subscription } from "../../app/services/billing"
import type { Usage } from "../../app/services/usage"
import { useSubscriptionQuery } from "../billing/useSubscription"
import { usageSummaryItemTypes } from "./usageSummaryItemTypes"
import { formatValue, type Unit } from "./usageUtils"
import { useUsageQuery } from "./useUsage"

interface UsageDetailsCardProps {
  itemTypeKey: keyof typeof usageSummaryItemTypes
  selectedDateRange: DateRange
  planLimitsKnown: boolean
}

export function UsageDetailsCard(props: UsageDetailsCardProps) {
  const { itemTypeKey, selectedDateRange, planLimitsKnown } = props

  const { data: subscription } = useSubscriptionQuery()
  const { data: rawUsage } = useUsageQuery(
    selectedDateRange.startDate,
    addDays(selectedDateRange.endDate, 1),
  )

  const { unit } = usageSummaryItemTypes[itemTypeKey]

  const { usage, overage } = calculateUsageAndOverage({
    itemTypeKey,
    rawUsage,
    planLimitsKnown,
    subscription,
  })

  const usageDetails = (
    <>
      <UsageDetailLine
        label="Used"
        value={usage.used}
        color="green8"
        unit={unit}
        showColorDot={planLimitsKnown}
      />
      {usage.included && (
        <UsageDetailLine
          label="Included"
          value={usage.included}
          color="green4"
          unit={unit}
        />
      )}
    </>
  )

  const overageDetails =
    overage && overage.included ? (
      <>
        <UsageDetailLine
          label="Used overage"
          value={overage.used}
          color="amber10"
          unit={unit}
        />
        <UsageDetailLine
          label="Allowed overage"
          value={overage.included}
          color="amber4"
          unit={unit}
        />
        <Separator />
        <UsageDetailLine
          label="Total used"
          value={usage.used + overage.used}
          unit={unit}
        />
        {usage.included ? (
          <UsageDetailLine
            label="Total included"
            value={usage.included + overage.included}
            unit={unit}
          />
        ) : null}
      </>
    ) : null

  return (
    <UsageDetailsCardTemplate
      usageDetails={usageDetails}
      overageDetails={overageDetails}
    />
  )
}

interface CalculateUsageAndOverageArgs {
  itemTypeKey: keyof typeof usageSummaryItemTypes
  rawUsage: Usage
  planLimitsKnown: boolean
  subscription: Subscription
}

function calculateUsageAndOverage(args: CalculateUsageAndOverageArgs) {
  const { itemTypeKey, rawUsage, planLimitsKnown, subscription } = args

  const itemType = usageSummaryItemTypes[itemTypeKey]
  const totalUsage = itemType.getTotalUsage(rawUsage)

  if (planLimitsKnown) {
    const quota = itemType.getQuota(subscription)
    const limit = itemType.getLimit(subscription)

    if (limit === "unlimited" || quota === "unlimited") {
      return { usage: { used: totalUsage } }
    } else {
      const quotaUsage = Math.min(quota, totalUsage)
      const overageUsage = totalUsage - quotaUsage

      return {
        usage: { used: quotaUsage, included: quota },
        overage: { used: overageUsage, included: limit - quota },
      }
    }
  } else {
    return { usage: { used: totalUsage } }
  }
}

interface UsageDetailsCardSkeletonProps {
  planLimitsKnown?: boolean
}

export function UsageDetailsCardSkeleton(props: UsageDetailsCardSkeletonProps) {
  const { planLimitsKnown = true } = props

  const valueSkeleton = <Skeleton width={80} height={16} color="grey5" />

  return (
    <UsageDetailsCardTemplate
      usageDetails={
        <>
          <UsageDetailLineTemplate
            label="Used"
            color="green8"
            rightContent={valueSkeleton}
            showColorDot={planLimitsKnown}
          />
          {planLimitsKnown && (
            <UsageDetailLineTemplate
              label="Included"
              color="green4"
              rightContent={valueSkeleton}
            />
          )}
        </>
      }
    />
  )
}

interface UsageDetailLineProps
  extends Pick<
    UsageDetailLineTemplateProps,
    "label" | "color" | "showColorDot"
  > {
  value: number
  unit: Unit
}

function UsageDetailLine(props: UsageDetailLineProps) {
  const { label, value, color, unit, showColorDot } = props

  const humanReadableValue = formatValue(value, { unit })

  const humanReadableTooltip = formatValue(value, {
    unit,
    notation: "standard",
  })

  return (
    <UsageDetailLineTemplate
      label={label}
      color={color}
      rightContent={
        <Tooltip content={humanReadableTooltip} sideOffset={4}>
          <Text variant="small" color="grey11">
            {humanReadableValue}
          </Text>
        </Tooltip>
      }
      showColorDot={showColorDot}
    />
  )
}

interface UsageDetailsCardTemplateProps {
  usageDetails: ReactNode
  overageDetails?: ReactNode
}

function UsageDetailsCardTemplate(props: UsageDetailsCardTemplateProps) {
  const { usageDetails, overageDetails } = props
  return (
    <Card padding={16} color="grey2" variant="outlined">
      <CardContent gap={16}>
        {usageDetails}
        {overageDetails && (
          <>
            <Separator />
            {overageDetails}
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface UsageDetailLineTemplateProps {
  label: string
  color?: ThemeKeys<"color">
  rightContent?: ReactNode
  showColorDot?: boolean
}

function UsageDetailLineTemplate(props: UsageDetailLineTemplateProps) {
  const {
    label,
    color = "transparent",
    rightContent,
    showColorDot = true,
  } = props

  return (
    <Box justifyContent="space-between">
      <Box gap={8} alignItems="center">
        {showColorDot ? (
          <Box
            backgroundColor={color}
            width={12}
            height={12}
            borderRadius={6}
          />
        ) : undefined}
        <Text variant="smallBold">{label}</Text>
      </Box>
      {rightContent}
    </Box>
  )
}
